import { Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { StyledTextField } from './StyledTextField';

export interface CompositeTextInputFieldInfoType {
  info: CompositeTextInputFieldType;
}

export interface CompositeTextInputFieldType {
  topLabelText?: string;
  placeHolderText: string;
  errorLabelText: string;
  failedValidationText: string;
  isRequiredField: boolean;
  isValidationNeeded: boolean;
  regularExpression?: RegExp;
  isUsedAsPhoneNumberInput: boolean;
  topLabelStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
    bottomMargin: string;
  };
  inputFieldStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    fontColor: string;
    isPlaceHolderItalic: boolean;
    lineHeight: string;
    placeHolderColor: string;
    borderColor: string;
    borderRadius: string;
    height: string;
    borderWidth: string;
  };
  errorLabelStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    color: string;
    lineHeight: string;
  };
  textChangeHandler?: (text: string) => void;
}

export const CompositeTextInputField = (props: CompositeTextInputFieldInfoType) => {
  const [isTextFieldFocusedOnce, setIsTextFieldFocusedOnce] = useState(false);
  const [isTypingStarted, setIsTypingStarted] = useState(false);
  const [text, setText] = useState('');
  const [isValidationSuccess, setIsValidationSuccess] = useState(true);

  // This is be executed when the state changes
  useEffect(() => {
    if (text !== '') {
      setIsTypingStarted(true);
    }
  }, [text]);

  // This is to be executed when the state changes are valid in order to update parent
  useEffect(() => {
    if (text !== '' && isValidationSuccess && props.info.textChangeHandler) {
      props.info.textChangeHandler(text);
    }
  }, [isValidationSuccess, props.info, text]);
  let invalidText = false;
  if (props.info.isRequiredField) {
    invalidText = isTypingStarted && text === '' && isTextFieldFocusedOnce;
  }

  const performValidation = (typedText: string) => {
    if (typedText.length > 0 && props.info.isValidationNeeded && props.info.regularExpression) {
      const regex = props.info.regularExpression;
      const typedTextFoundInRegex = regex.test(typedText);
      if (typedTextFoundInRegex) {
        setIsValidationSuccess(false);
      } else {
        setIsValidationSuccess(true);
      }
    } else {
      setIsValidationSuccess(true);
    }
  };

  function formatPhoneNumber(p: string) {
    const phone = p.replace(/[^\d]/g, '');
    const pLength = phone.length;
    if (pLength < 4) {
      return phone;
    } else if (pLength < 7) {
      return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    } else {
      return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
    }
  }

  return (
    <Grid item container direction={'column'}>
      <Grid item>
        <Typography
          gutterBottom
          sx={{
            ...props.info.topLabelStyle,
          }}
        >
          {props.info.topLabelText}
        </Typography>
      </Grid>
      <Grid item>
        <StyledTextField
          placeHolderColor={props.info.inputFieldStyle.placeHolderColor}
          placeholder={props.info.placeHolderText}
          borderColor={props.info.inputFieldStyle.borderColor}
          borderRadius={props.info.inputFieldStyle.borderRadius}
          fontFamily={props.info.inputFieldStyle.fontFamily}
          fontColor={props.info.inputFieldStyle.fontColor}
          height={props.info.inputFieldStyle.height}
          borderWidth={props.info.inputFieldStyle.borderWidth}
          fullWidth
          label=""
          variant="outlined"
          error={invalidText || !isValidationSuccess}
          isItalic={props.info.inputFieldStyle.isPlaceHolderItalic}
          value={text}
          onFocus={() => setIsTextFieldFocusedOnce(true)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const typedText = event.target.value;
            if (props.info.isUsedAsPhoneNumberInput) {
              setText(formatPhoneNumber(typedText));
            } else {
              setText(typedText);
            }
            performValidation(typedText);
          }}
        />
      </Grid>
      <Grid item>
        {props.info.isRequiredField && invalidText && (
          <Typography
            gutterBottom
            sx={{
              ...props.info.errorLabelStyle,
              marginBottom: '0rem',
            }}
          >
            {props.info.errorLabelText}
          </Typography>
        )}
        {!isValidationSuccess && (
          <Typography
            gutterBottom
            sx={{
              ...props.info.errorLabelStyle,
              marginBottom: '0rem',
            }}
          >
            {props.info.failedValidationText}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
